import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `COL-DEADLINE` (@UC0196) — thu đều **CÓ HẠN CHÓT NGƯỜI DÙNG NÓI**, T2 ghi tiền. QĐ PO 15/09: GIỮ — bỏ thì hạn
 * người gõ bị **rơi lặng**. Executor = `AdhocCollectionService.createWithMode` với `amountMode: "equal"` — hàm của cửa
 * `POST /v1/funds/:id/collections/advanced`, cửa mà **không client nào gọi** (mobile mở đợt có hạn qua `POST /funds/{fundId}/collections`
 * = `createAdhoc`, cửa của `COL-EQUAL`). Hai hàm KHÁC nhau nhưng mang **CÙNG luật phạm vi**: đợt chia đều cả nhóm ⇒ `appliesToAllMembers =
 * true` ⇒ người vào nhóm sau cũng có khoản (`adhocAppliesToAllMembers` — COL-DEADLINE-ENROLL; trước đó cửa này để mặc định `false`).
 *
 * 🔴🔴 **VÌ SAO KHÔNG THỪA BÊN CẠNH `COL-EQUAL`** — câu hỏi đầu tiên phải trả lời, vì hai trang ghi vào **cùng một
 * bảng `collections`**, cùng sinh `member_dues`. Khác nhau ở **AI QUYẾT HẠN**, và khác biệt đó có hậu quả:
 *
 * | | `COL-EQUAL` (@UC0195) | `COL-DEADLINE` (trang này) |
 * |---|---|---|
 * | ai quyết hạn | **MÁY** — cuối tháng bấm Đồng ý | **NGƯỜI DÙNG** nói ra |
 * | ô hạn | **không có** | `hanChot` (`date`, bắt buộc) |
 * | hạn trên phiếu | **HẰNG không-cụ-thể** + `hanThat` báo lại sau | **ngày CỤ THỂ**, in thẳng |
 * | executor | `createAdhoc` | `createWithMode` |
 *
 * 🔴 **Và đây là lý do KHÔNG chép khuôn `hanGhi`/`hanThat` sang đây.** US-AIV2-0080 phải gỡ ngày khỏi phiếu của
 * `COL-EQUAL` vì ngày ở đó **suy từ `now`**, mà phiếu và lượt ghi là **hai lượt HTTP** ⇒ ~12 đêm cuối tháng mỗi năm
 * là mìn `FACTS_MISMATCH`. Ở trang này ngày là **ô đã resolve, ĐÓNG BĂNG trên phiếu** — không chạm đồng hồ ⇒ **lớp
 * lệch đó không tồn tại**, và phiếu **được phép** (và phải) in ngày cụ thể. Dọn `{hanChot}` đi "cho giống
 * `COL-EQUAL`" là **gỡ đúng thứ người dùng vừa gõ**, vì một lý do không áp cho trang này.
 *
 * 🔴 **`executorMayNoop` — `createWithMode` REPLAY LẶNG.** Khoá idempotency derive TỪ NỘI DUNG
 * (`deriveCollectionCreateKey(ADVANCED, fundId, {title, dueDate, amountMode, amountMinor, …})`, `:662`) và gặp lại
 * khoá thì `withCreateIdempotency` → `findCollectionCreateReplay` trả **nguyên kết quả lần trước**, ghi
 * `audit.diff.replay = true`, **0 đợt thu mới, 0 `member_dues` mới**. Vì facts lần trước BẰNG facts vừa preview
 * (khoá ký trên chính những con số đó), câu `done` render ra **giống hệt** lượt ghi thật ⇒ nhóm tin cả lớp vừa nhận
 * nghĩa vụ trong khi **không ai nợ một đồng**. Đúng ca #832 HIGH-1 của `COL-EQUAL`; câu `doneAlready` sinh ra để xoá
 * lời nói dối đó, và phải **dạy cửa thoát** (đặt tên khác / đổi hạn ⇒ khoá khác ⇒ đợt thứ hai CÓ THẬT).
 *
 * 🔴 **§3.7 — trang chỉ tạo NGHĨA VỤ (1).** Mở đợt thu sinh `member_dues` ở `pending`: **không** khai hộ ai đã
 * chuyển (2), **không** xác nhận đã nhận (3). Ở tầng Sổ: **0 ô `member`**, 0 ô mang nghĩa "đã đóng", và câu chữ
 * tuyệt đối không hứa tiền đã vào quỹ. Gộp (1) với (2)/(3) ở đây là mở một đợt thu mà sổ tưởng đã thu xong.
 *
 * 🔴 **Ô `nhom` không phải trang trí** (QĐ PO 17/09 16:05, cùng luật `COL-EQUAL`/`RECORD-PAID`): ô `quy` loại `fund`
 * có `AI_SLOT_TYPE_NEEDS_GROUP.fund = true`, mà tab trợ lý là tab TOÀN CỤC ⇒ không khai ô nhóm thì `assertCanOpen`
 * trả 403 `AI_GROUP_REQUIRED` và `allowedPages` lọc trang khỏi menu. Bản `draft` thiếu ô này.
 *
 * ⚠️ **KHÔNG khai `endpoints`**: cửa `POST /funds/{fundId}/collections` mà mobile gọi thuộc **`COL-EQUAL`** (mỗi cửa
 * đúng một trang — `ENDPOINT_CLAIMED_TWICE`), còn cửa `…/advanced` không có client. Mảng rỗng là lời khai hợp lệ: trang là biến thể
 * NGHIỆP VỤ của đợt thu đều (cùng bảng, cùng luật phạm vi), không phải cùng một cửa HTTP.
 */
export const COL_DEADLINE = {
  id: "COL-DEADLINE",
  uc: "@UC0196",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    // Ô nhóm ĐẦU TIÊN: phạm vi ô `quy` phụ thuộc nhóm đã chốt.
    SLOT_GROUP,
    SLOT_FUND,
    { id: "soTien", type: "money", required: true, ask: "Mỗi người đóng bao nhiêu?" },
    // 🔴 Ô PHÂN BIỆT trang này với COL-EQUAL — bắt buộc, và giá trị của nó đóng băng trên phiếu.
    { id: "hanChot", type: "date", required: true, ask: "Hạn chót đóng là ngày nào?" },
    { id: "tenDot", type: "text", required: false, ask: "Đặt tên đợt thu là gì? (bỏ trống thì lấy theo hạn)" },
  ],
  llm: {
    description: "Mở đợt thu đều mà người mở NÓI RÕ hạn chót; quá hạn thì khoản chưa đóng thành nợ và có thể nhắc.",
    examples: [
      "thu 200k mỗi người, hạn cuối tháng",
      "thu 200k moi nguoi han cuoi thang",
      "mở đợt thu 500k đóng trước 30/9",
      "mo dot thu 500k truoc 30/9",
      "thu quỹ có hạn chót 15 tháng sau",
      "thu quy han 15 thang sau",
      "đóng 300k trước thứ sáu",
      "dong 300k truoc thu sau",
      "thu tiền dã ngoại 250k, deadline tuần sau",
      "thu 100k hạn 20/10",
      "mở đợt thu có hạn",
      "thu 200k hạn cuối tháng",
      // ——— 0105: thêm cho đủ 40 ———
      "mo dot thu co han",
      "thu 100k han 20/10",
      "thu tien da ngoai 250k deadline tuan sau",
      "mỗi bạn đóng 150k, hạn chót mùng 5",
      "moi ban dong 150k han chot mung 5",
      "tạo đợt thu 400k, phải nộp xong trước 25 tháng này",
      "tao dot thu 400k nop xong truoc 25 thang nay",
      "thu quỹ lớp 200k, ai cũng đóng trước ngày 10 nhé",
      "thu quy lop 200k dong truoc ngay 10",
      "mở đợt thu 1 triệu, hạn chót 31/12",
      "mo dot thu 1 trieu han chot 31/12",
      "thu tiền áo đồng phục 180k, hạn 2 tuần nữa",
      "thu tien ao dong phuc 180k han 2 tuan nua",
      "đợt thu mới 300k mỗi người, chốt vào chủ nhật",
      "dot thu moi 300k moi nguoi chot vao chu nhat",
      "mình muốn mở đợt thu có hạn nộp cụ thể",
      "minh muon mo dot thu co han nop cu the",
      "thu 500k một bạn, hạn cuối tuần này",
      "thu 500k mot ban han cuoi tuan nay",
      "thu quỹ đầu năm 250k, hạn trước tết",
      "thu quy dau nam 250k han truoc tet",
      "mở thu 120k, hạn nộp ngày 15 hàng tháng à không, ngày 15 tháng này",
      "tạo đợt thu có deadline",
      "tao dot thu co deadline",
      "thu 350k mỗi phụ huynh trước ngày 8",
      "thu 350k moi phu huynh truoc ngay 8",
      "thu 600k mỗi người, hạn nộp mùng 1 tháng sau",
      "thu 600k moi nguoi han nop mung 1 thang sau",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["đợt thu quỹ đang mở tới ngày nào"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền, hoặc mơ hồ bậc (§4).
   */
  evalAmounts: {
    "thu 200k mỗi người, hạn cuối tháng": "200000",
    "thu 200k moi nguoi han cuoi thang": "200000",
    "mở đợt thu 500k đóng trước 30/9": "500000",
    "mo dot thu 500k truoc 30/9": "500000",
    "đóng 300k trước thứ sáu": "300000",
    "dong 300k truoc thu sau": "300000",
    "thu tiền dã ngoại 250k, deadline tuần sau": "250000",
    "thu 100k hạn 20/10": "100000",
    "thu 200k hạn cuối tháng": "200000",
    "thu 100k han 20/10": "100000",
    "thu tien da ngoai 250k deadline tuan sau": "250000",
    "mỗi bạn đóng 150k, hạn chót mùng 5": "150000",
    "moi ban dong 150k han chot mung 5": "150000",
    "tạo đợt thu 400k, phải nộp xong trước 25 tháng này": "400000",
    "tao dot thu 400k nop xong truoc 25 thang nay": "400000",
    "thu quỹ lớp 200k, ai cũng đóng trước ngày 10 nhé": "200000",
    "thu quy lop 200k dong truoc ngay 10": "200000",
    "mở đợt thu 1 triệu, hạn chót 31/12": "1000000",
    "mo dot thu 1 trieu han chot 31/12": "1000000",
    "thu tiền áo đồng phục 180k, hạn 2 tuần nữa": "180000",
    "thu tien ao dong phuc 180k han 2 tuan nua": "180000",
    "đợt thu mới 300k mỗi người, chốt vào chủ nhật": "300000",
    "dot thu moi 300k moi nguoi chot vao chu nhat": "300000",
    "thu 500k một bạn, hạn cuối tuần này": "500000",
    "thu 500k mot ban han cuoi tuan nay": "500000",
    "thu quỹ đầu năm 250k, hạn trước tết": "250000",
    "thu quy dau nam 250k han truoc tet": "250000",
    "mở thu 120k, hạn nộp ngày 15 hàng tháng à không, ngày 15 tháng này": "120000",
    "thu 350k mỗi phụ huynh trước ngày 8": "350000",
    "thu 350k moi phu huynh truoc ngay 8": "350000",
    "thu 600k mỗi người, hạn nộp mùng 1 tháng sau": "600000",
    "thu 600k moi nguoi han nop mung 1 thang sau": "600000",
  },
  executor: "adhocCollection.createWithMode",
  // 🔴 Cửa REPLAY LẶNG khi trùng khoá nội dung — xem docblock. Thiếu cờ ⇒ lượt 0-ghi nói "Đã mở đợt thu".
  executorMayNoop: true,
  /**
   * AI-COL-OPEN-PHRASING (@UC0195 @UC0220): lưới mở đợt thu theo đầu người — kiểu đợt + ô nhận chữ của câu (`collection-open.ts`). Không vào prompt ⇒ 0 ca eval đổi hash.
   * UX-COLLECTION-PERIOD (@UC0196): `due` — câu nói một THÁNG ("thu quỹ tháng 11 mỗi người 150k") ⇒ trang này (lưới kỳ chuyển từ COL-EQUAL), hạn = ngày cuối
   * tháng đó, tên "Thu quỹ tháng 11/YYYY" (`collection-period.ts`). Hạn ĐÓNG BĂNG trong ô ⇒ thẻ in ngày cụ thể (lớp lệch A5 không tồn tại ở trang này).
   */
  collectionFromSentence: { cadence: "deadline", amount: "soTien", name: "tenDot", due: "hanChot" },
  /** `@UC0216` — cửa `POST /funds/{fundId}/collections` thuộc `COL-EQUAL` (chủ của cửa đó); trang này là biến thể. */
  endpoints: [],
  /** `hanChot` KHÔNG ở đây — nó là Ô (đóng băng trên phiếu). Khai thêm thành fact là dựng nguồn thứ hai cho cùng một giá trị. */
  facts: ["soNguoi", "tongTien", "tenDotGhi"],
  templates: {
    // Thứ tự chỗ trống = thứ tự DÒNG PHIẾU: nhóm → quỹ → tên → mỗi người → hạn → số người → tổng.
    confirm: "Mở đợt thu “{tenDotGhi}” ở nhóm {nhom}, quỹ {quy}: mỗi người {soTien}, hạn {hanChot}, {soNguoi} người, tổng {tongTien}?",
    /**
     * Câu báo xong dùng lại `{hanChot}` — **ô**, nên nó là CÙNG một giá trị đã in trên phiếu, không phải một phép
     * đo thứ hai. Khác `COL-EQUAL` (ở đó `done` phải đọc `hanThat` vì ngày do máy sinh lúc ghi).
     */
    done: "Đã mở đợt thu “{tenDotGhi}” — {soNguoi} người × {soTien} = {tongTien}, hạn {hanChot}. Chưa ai đóng; quá hạn thì khoản chưa đóng tính là nợ, muốn nhắc thì nói “nhắc mọi người đóng”.",
    /**
     * 🔴 Lượt KHÔNG ghi gì (replay khoá nội dung). Ba việc câu này phải làm, theo thứ tự người đọc cần:
     *  1. nói thẳng **KHÔNG tạo thêm** — không để người dùng phải suy ra từ chữ "đã";
     *  2. chỉ ra đợt ĐÃ CÓ khớp ở điểm nào (tên · số tiền · hạn — ba thứ khoá ký lên, nên ba thứ này chắc đúng);
     *  3. mở **cửa thoát gõ lại được**: đổi tên **hoặc** đổi hạn ⇒ khoá khác ⇒ đợt thứ hai CÓ THẬT.
     * CỐ Ý không mở đầu bằng "Đã mở" — đó chính là lời nói dối mà `doneAlready` sinh ra để xoá.
     */
    doneAlready:
      "Đợt thu “{tenDotGhi}” ({soTien} mỗi người, hạn {hanChot}) đã có trong quỹ {quy} rồi, nên mình KHÔNG tạo thêm. Muốn mở một đợt nữa thì đặt tên khác hoặc chọn hạn khác, ví dụ: “thu {soTien} mỗi người, hạn {hanChot}, tên là Dã ngoại”.",
  },
  /** P5.3 — nhãn NGƯỜI ĐỌC cho từng dòng phiếu T2; thiếu một nhãn thì `renderConfirm` ném (không camelCase lên màn tiền). */
  labels: {
    nhom: "Nhóm",
    quy: "Quỹ",
    tenDotGhi: "Tên đợt thu",
    soTien: "Mỗi người",
    hanChot: "Hạn đóng",
    soNguoi: "Số người",
    tongTien: "Tổng thu",
  },
  journey: { outcomes: { done: { next: ["DEBT-REMIND", "READ-COLLECTIONS"] }, done_already: { next: ["READ-COLLECTIONS", "DEBT-REMIND"] } } },
  /** UX-OPEN-TASK-EDIT (@UC0165): lời sửa gắn trang anh em (cùng hành động MỞ ĐỢT THU, khác kiểu hạn) áp được vào phiếu đang mở của trang này. */
  editPeers: ["COL-EQUAL"],
  help: { title: "Thu đều có hạn chót", tryNow: "thu 200k mỗi người, hạn cuối tháng" },
} satisfies AiCapabilityPage;
